import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../App';
import toast from 'react-hot-toast';

const SECTION_ICONS = {
  header: 'fa-heading', property_assessment: 'fa-house', system_design: 'fa-solar-panel',
  financial_analysis: 'fa-calculator', savings_projection: 'fa-piggy-bank', environmental_impact: 'fa-leaf',
  testimonials: 'fa-quote-left', warranty: 'fa-shield-halved', terms: 'fa-file-contract', signature: 'fa-signature',
};

const SECTION_COLORS = {
  header: '#3B82F6', property_assessment: '#8B5CF6', system_design: '#16A34A',
  financial_analysis: '#F59E0B', savings_projection: '#EC4899', environmental_impact: '#22C55E',
  testimonials: '#6366F1', warranty: '#0EA5E9', terms: '#64748B', signature: '#DC2626',
};

export default function ProposalBuilder() {
  const { id } = useParams();
  const [proposal, setProposal] = useState(null);
  const [sections, setSections] = useState([]);
  const [branding, setBranding] = useState({ primary_color: '#16A34A', company_logo: '', tagline: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get(`/api/proposals/${id}/builder`).then(res => {
      setProposal(res.data.proposal);
      setSections(res.data.builder.sections || []);
      if (res.data.builder.custom_branding) setBranding(prev => ({ ...prev, ...res.data.builder.custom_branding }));
      if (res.data.proposal.share_token) setShareLink(`${window.location.origin}/portal/${res.data.proposal.share_token}`);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load proposal'); setLoading(false); });
  }, [id]);

  const handleDragStart = (idx) => setDragItem(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverItem(idx); };
  const handleDrop = () => {
    if (dragItem === null || dragOverItem === null || dragItem === dragOverItem) { setDragItem(null); setDragOverItem(null); return; }
    const copy = [...sections];
    const [removed] = copy.splice(dragItem, 1);
    copy.splice(dragOverItem, 0, removed);
    copy.forEach((s, i) => s.order = i);
    setSections(copy);
    setDragItem(null);
    setDragOverItem(null);
  };

  const toggleSection = (idx) => {
    const copy = [...sections];
    copy[idx].enabled = !copy[idx].enabled;
    setSections(copy);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/proposals/${id}/builder`, { proposal_id: id, sections, custom_branding: branding, notes: '' });
      toast.success('Proposal builder saved!');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await api.post(`/api/proposals/${id}/share`);
      const link = `${window.location.origin}/portal/${res.data.share_token}`;
      setShareLink(link);
      navigator.clipboard.writeText(link).then(() => toast.success('Share link copied!'));
    } catch { toast.error('Failed to create share link'); }
    setSharing(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;
  if (!proposal) return <div className="text-center py-20 text-text-muted">Proposal not found</div>;

  const enabledSections = sections.filter(s => s.enabled);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="proposal-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/proposals" className="p-2 rounded-xl hover:bg-surface-alt transition-colors" data-testid="back-to-proposals"><i className="fa-solid fa-arrow-left text-text-muted" /></Link>
          <div>
            <h1 className="font-heading text-2xl font-bold text-text-primary">Proposal Builder</h1>
            <p className="text-text-muted text-sm">{proposal.lead_name} &middot; {proposal.lead_city}, {proposal.lead_state}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreviewMode(!previewMode)} className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${previewMode ? 'bg-primary text-white' : 'btn-secondary'}`} data-testid="toggle-preview">
            <i className={`fa-solid ${previewMode ? 'fa-pen' : 'fa-eye'} mr-1.5`} />{previewMode ? 'Edit Mode' : 'Preview'}
          </button>
          <button onClick={handleShare} disabled={sharing} className="btn-secondary text-xs py-2 px-4" data-testid="share-proposal-btn">
            {sharing ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-share-nodes mr-1.5" />Share</>}
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-xs py-2 px-4" data-testid="save-builder-btn">
            {saving ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-save mr-1.5" />Save</>}
          </button>
        </div>
      </div>

      {shareLink && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
          <i className="fa-solid fa-link text-primary text-sm" />
          <input readOnly value={shareLink} className="flex-1 bg-transparent text-sm text-text-primary font-mono truncate outline-none" />
          <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success('Copied!'); }} className="text-xs text-primary font-semibold hover:underline" data-testid="copy-share-link">Copy</button>
        </div>
      )}

      <div className={`grid ${previewMode ? '' : 'lg:grid-cols-3'} gap-6`}>
        {/* Section Panel */}
        {!previewMode && (
          <div className="lg:col-span-1 space-y-4">
            {/* Branding */}
            <div className="card-interactive p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">Custom Branding</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-text-muted mb-1 block">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={branding.primary_color} onChange={e => setBranding({...branding, primary_color: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" />
                    <input value={branding.primary_color} onChange={e => setBranding({...branding, primary_color: e.target.value})} className="input-field h-8 text-xs flex-1 font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-text-muted mb-1 block">Tagline</label>
                  <input value={branding.tagline} onChange={e => setBranding({...branding, tagline: e.target.value})} className="input-field h-8 text-xs" placeholder="Your energy, reimagined" />
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="card-interactive p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">Sections <span className="text-primary">({enabledSections.length})</span></h3>
              <p className="text-[10px] text-text-muted mb-3">Drag to reorder &middot; Toggle to include/exclude</p>
              <div className="space-y-1.5">
                {sections.map((section, idx) => (
                  <div
                    key={section.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={handleDrop}
                    onDragEnd={() => { setDragItem(null); setDragOverItem(null); }}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-grab active:cursor-grabbing ${
                      dragOverItem === idx ? 'border-primary bg-primary/5' : section.enabled ? 'border-border bg-white hover:border-primary/20' : 'border-border/50 bg-surface-alt/50 opacity-60'
                    }`}
                    data-testid={`section-${section.id}`}
                  >
                    <i className="fa-solid fa-grip-vertical text-text-muted text-[10px]" />
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (SECTION_COLORS[section.type] || '#64748B') + '20' }}>
                      <i className={`fa-solid ${SECTION_ICONS[section.type] || 'fa-file'} text-[10px]`} style={{ color: SECTION_COLORS[section.type] || '#64748B' }} />
                    </div>
                    <span className="flex-1 text-xs font-semibold text-text-primary truncate">{section.title}</span>
                    <button onClick={() => toggleSection(idx)} className={`w-9 h-5 rounded-full transition-all flex-shrink-0 ${section.enabled ? 'bg-primary' : 'bg-gray-200'}`} data-testid={`toggle-${section.id}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${section.enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Preview */}
        <div className={previewMode ? '' : 'lg:col-span-2'}>
          <div className="card-interactive overflow-hidden" data-testid="proposal-preview">
            {/* Header Section */}
            {enabledSections.find(s => s.type === 'header') && (
              <div className="p-8 text-center text-white" style={{ background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.primary_color}CC)` }}>
                <h2 className="text-3xl font-heading font-bold mb-1">{proposal.company_name || 'Samaritan Solar'}</h2>
                {branding.tagline && <p className="text-sm opacity-90">{branding.tagline}</p>}
                <p className="text-xs opacity-70 mt-2">Solar Energy Proposal</p>
              </div>
            )}

            <div className="p-6 space-y-6">
              {/* Property Assessment */}
              {enabledSections.find(s => s.type === 'property_assessment') && (
                <div className="border border-border rounded-xl p-5">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-house text-primary" />Property Assessment</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { l: 'Client', v: proposal.lead_name }, { l: 'Address', v: `${proposal.lead_address}, ${proposal.lead_city}` },
                      { l: 'Property Type', v: proposal.property_type }, { l: 'Roof Age', v: `${proposal.roof_age || 'N/A'} years` },
                      { l: 'Solar Potential', v: proposal.solar_potential }, { l: 'Annual Energy', v: `${(proposal.annual_kwh || 0).toLocaleString()} kWh` },
                    ].map(i => (
                      <div key={i.l}><p className="text-[10px] text-text-muted uppercase">{i.l}</p><p className="text-sm font-semibold text-text-primary">{i.v}</p></div>
                    ))}
                  </div>
                </div>
              )}

              {/* System Design */}
              {enabledSections.find(s => s.type === 'system_design') && (
                <div className="border border-border rounded-xl p-5">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-solar-panel text-primary" />System Design</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    {[
                      { l: 'System Size', v: `${proposal.system_size_kw} kW`, icon: 'fa-bolt' },
                      { l: 'Panels', v: `${proposal.panel_count}`, icon: 'fa-solar-panel' },
                      { l: 'Production', v: `${(proposal.annual_kwh || 0).toLocaleString()} kWh`, icon: 'fa-chart-line' },
                      { l: 'Warranty', v: `${proposal.warranty_years || 25} yrs`, icon: 'fa-shield' },
                    ].map(i => (
                      <div key={i.l} className="p-3 rounded-lg bg-surface-alt">
                        <i className={`fa-solid ${i.icon} text-primary text-lg mb-1`} />
                        <p className="text-xl font-heading font-bold text-text-primary">{i.v}</p>
                        <p className="text-[10px] text-text-muted">{i.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial Analysis */}
              {enabledSections.find(s => s.type === 'financial_analysis') && (
                <div className="border border-border rounded-xl p-5">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-calculator" style={{color: '#F59E0B'}} />Financial Analysis</h3>
                  <div className="space-y-2">
                    {[
                      { l: 'Total System Cost', v: `$${(proposal.total_cost || 0).toLocaleString()}` },
                      { l: 'Federal & State Incentives (30%)', v: `-$${(proposal.incentives || 0).toLocaleString()}`, green: true },
                      { l: 'Net Cost After Incentives', v: `$${(proposal.net_cost || 0).toLocaleString()}`, bold: true },
                    ].map(i => (
                      <div key={i.l} className={`flex justify-between items-center py-2 ${i.bold ? 'border-t-2 border-text-primary pt-3' : 'border-b border-border/50'}`}>
                        <span className={`text-sm ${i.bold ? 'font-bold text-text-primary' : 'text-text-secondary'}`}>{i.l}</span>
                        <span className={`text-sm font-mono font-bold ${i.green ? 'text-primary' : 'text-text-primary'}`}>{i.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Savings */}
              {enabledSections.find(s => s.type === 'savings_projection') && (
                <div className="border border-border rounded-xl p-5" style={{ borderColor: branding.primary_color + '30' }}>
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-piggy-bank text-pink-500" />Savings Projection</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 rounded-xl" style={{ backgroundColor: branding.primary_color + '10' }}>
                      <p className="text-2xl font-heading font-bold" style={{ color: branding.primary_color }}>${(proposal.annual_savings || 0).toLocaleString()}</p>
                      <p className="text-xs text-text-muted">Annual Savings</p>
                    </div>
                    <div className="p-4 rounded-xl bg-surface-alt">
                      <p className="text-2xl font-heading font-bold text-text-primary">${((proposal.annual_savings || 0) * 25).toLocaleString()}</p>
                      <p className="text-xs text-text-muted">25-Year Savings</p>
                    </div>
                    <div className="p-4 rounded-xl bg-surface-alt">
                      <p className="text-2xl font-heading font-bold text-text-primary">{proposal.net_cost && proposal.annual_savings ? Math.round(proposal.net_cost / proposal.annual_savings) : 'N/A'}</p>
                      <p className="text-xs text-text-muted">Payback (Years)</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Environmental */}
              {enabledSections.find(s => s.type === 'environmental_impact') && (
                <div className="border border-green-200 rounded-xl p-5 bg-green-50/30">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-leaf text-green-500" />Environmental Impact</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div><p className="text-2xl font-heading font-bold text-green-600">{((proposal.annual_kwh || 0) * 0.0004 * 25).toFixed(0)}</p><p className="text-xs text-text-muted">Tons CO2 Offset</p></div>
                    <div><p className="text-2xl font-heading font-bold text-green-600">{Math.round((proposal.annual_kwh || 0) * 0.0004 * 25 / 0.06)}</p><p className="text-xs text-text-muted">Trees Equivalent</p></div>
                    <div><p className="text-2xl font-heading font-bold text-green-600">{((proposal.annual_kwh || 0) * 25 / 1000).toFixed(0)}K</p><p className="text-xs text-text-muted">kWh Clean Energy</p></div>
                  </div>
                </div>
              )}

              {/* Testimonials */}
              {enabledSections.find(s => s.type === 'testimonials') && (
                <div className="border border-border rounded-xl p-5">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-quote-left text-indigo-500" />Customer Reviews</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { name: 'Sarah M.', city: 'Los Angeles', text: 'Our electricity bill went from $280 to $12. Incredible ROI!', stars: 5 },
                      { name: 'Robert K.', city: 'San Diego', text: 'Professional installation and fantastic customer support.', stars: 5 },
                    ].map(t => (
                      <div key={t.name} className="p-4 rounded-xl bg-surface-alt">
                        <div className="flex gap-0.5 mb-2">{[...Array(t.stars)].map((_, i) => <i key={i} className="fa-solid fa-star text-yellow-400 text-xs" />)}</div>
                        <p className="text-sm text-text-secondary italic mb-2">"{t.text}"</p>
                        <p className="text-xs font-semibold text-text-primary">{t.name} <span className="text-text-muted font-normal">&middot; {t.city}</span></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warranty */}
              {enabledSections.find(s => s.type === 'warranty') && (
                <div className="border border-border rounded-xl p-5">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-shield-halved text-sky-500" />Warranty & Guarantees</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { l: '25-Year Panel Warranty', desc: 'Full manufacturer coverage on all solar panels', icon: 'fa-solar-panel' },
                      { l: '10-Year Inverter Warranty', desc: 'Extended inverter protection included', icon: 'fa-plug' },
                      { l: 'Workmanship Guarantee', desc: 'Lifetime guarantee on installation quality', icon: 'fa-wrench' },
                    ].map(w => (
                      <div key={w.l} className="p-3 rounded-lg bg-surface-alt text-center">
                        <i className={`fa-solid ${w.icon} text-sky-500 text-lg mb-2`} />
                        <p className="text-xs font-bold text-text-primary">{w.l}</p>
                        <p className="text-[10px] text-text-muted mt-1">{w.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Terms */}
              {enabledSections.find(s => s.type === 'terms') && (
                <div className="border border-border rounded-xl p-5">
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-file-contract text-gray-500" />Terms & Conditions</h3>
                  <div className="text-xs text-text-secondary space-y-2 leading-relaxed">
                    <p>1. This proposal is valid for 30 days from the date of issue.</p>
                    <p>2. Price includes all equipment, permits, installation, and system activation.</p>
                    <p>3. Federal Investment Tax Credit (ITC) of 30% is subject to change based on federal legislation.</p>
                    <p>4. Actual energy production may vary based on weather, shading, and system orientation.</p>
                    <p>5. Customer is responsible for securing any necessary HOA approvals.</p>
                  </div>
                </div>
              )}

              {/* Signature */}
              {enabledSections.find(s => s.type === 'signature') && (
                <div className="border-2 border-dashed border-primary/30 rounded-xl p-6 text-center bg-primary/5">
                  <i className="fa-solid fa-signature text-primary text-2xl mb-3" />
                  <h3 className="font-heading text-lg font-bold text-text-primary mb-1">Ready to Go Solar?</h3>
                  <p className="text-sm text-text-muted mb-4">Share this proposal with your client for electronic acceptance</p>
                  <button onClick={handleShare} className="btn-primary text-sm py-2.5 px-6" data-testid="share-for-signing"><i className="fa-solid fa-share-nodes mr-2" />Generate Client Portal Link</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
